/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
    Box, Typography, Stack, Alert, Chip,
    CircularProgress, Button,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Iconify from '../Iconify';
import { callApi } from '../../utils/apiCall';
import useSnackbar from '../../hooks/useCustomSnackbar';
import { sectionCardStyle, sectionLabelStyle, slotCardStyle, slotTimeStyle } from '../../styles/DentallyBookingStyle';

const MORNING_CUTOFF_HOUR = 12;
const AFTERNOON_CUTOFF_HOUR = 17;

function groupSlotsByPeriod(slots) {
    const morning = [];
    const afternoon = [];
    const evening = [];
    slots.forEach((slot) => {
        const hour = new Date(slot.start_time).getHours();
        if (hour < MORNING_CUTOFF_HOUR) morning.push(slot);
        else if (hour < AFTERNOON_CUTOFF_HOUR) afternoon.push(slot);
        else evening.push(slot);
    });
    return { morning, afternoon, evening };
}

function formatSlotTime(isoString) {
    try {
        return new Date(isoString).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch {
        return isoString;
    }
}

function SlotPeriodSection({ label, icon, iconColor, slots, selectedSlot, onSelect }) {
    if (!slots.length) return null;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75, px: 0.5 }}>
                <Iconify icon={icon} width={14} sx={{ color: iconColor }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                    {label}
                </Typography>
                <Chip
                    label={slots.length}
                    size="small"
                    sx={{ fontSize: '10px', height: 18, fontWeight: 600, bgcolor: 'rgba(145,158,171,0.08)', color: 'text.disabled' }}
                />
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 0.75 }}>
                {slots.map((slot) => {
                    const isSelected = selectedSlot?.start_time === slot.start_time && selectedSlot?.practitioner_id === slot.practitioner_id;
                    return (
                        <Box
                            key={`${slot.start_time}-${slot.practitioner_id}`}
                            onClick={() => onSelect(slot)}
                            sx={{
                                ...slotCardStyle(isSelected, iconColor),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                minHeight: 40,
                            }}
                        >
                            <Typography sx={slotTimeStyle}>{formatSlotTime(slot.start_time)}</Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

/**
 * PMS Availability Wizard Step — embedded within the standard CRM follow-up flow.
 *
 * Active when pmsEnabledGlobal=true AND dentallyEnabledGlobal=false.
 *
 * Mirrors the visual style of DentallyTreatmentAvailabilityWizardStep.
 *
 * Props (controlled from parent):
 *   practiceId          — required
 *   pmsSlot             — currently selected slot object (or null)
 *   onSlotSelected      — (slot | null) => void
 *   selectedPractitioner — practitioner object (or null)
 *   onPractitionerSelected — (practitioner | null) => void
 *   selectedDate        — Date object (or null)
 *   onDateSelected      — (Date | null) => void
 *   appointmentDuration — integer minutes (default 30)
 *   handleNext          — called when wizard "Next" fires
 */
const PmsAvailabilityWizardStep = forwardRef(({
    handleNext: onSubmit,
    practiceId,
    pmsSlot,
    onSlotSelected,
    selectedPractitioner,
    onPractitionerSelected,
    selectedDate,
    onDateSelected,
    appointmentDuration = 30,
}, ref) => {
    const { enqueueSnackbar } = useSnackbar();

    const [practitioners, setPractitioners] = useState([]);
    const [practitionersLoading, setPractitionersLoading] = useState(false);
    const [practitionersError, setPractitionersError] = useState(null);

    const [schedules, setSchedules] = useState([]);
    const [schedulesLoading, setSchedulesLoading] = useState(false);

    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [slotsError, setSlotsError] = useState(null);

    // Load practitioners on mount
    useEffect(() => {
        if (!practiceId) return undefined;
        let cancelled = false;
        setPractitionersLoading(true);
        setPractitionersError(null);

        callApi(`/api/v1/practices/${practiceId}/pms/practitioners/`)
            .then((r) => {
                if (cancelled) return;
                const results = r?.data?.results ?? r?.data ?? [];
                setPractitioners(Array.isArray(results) ? results : []);
            })
            .catch(() => {
                if (!cancelled) setPractitionersError('Failed to load practitioners. Please try again.');
            })
            .finally(() => {
                if (!cancelled) setPractitionersLoading(false);
            });

        return () => { cancelled = true; };
    }, [practiceId]);

    // Load schedules when practitioner changes
    useEffect(() => {
        if (!practiceId || !selectedPractitioner?.id) {
            setSchedules([]);
            return undefined;
        }
        let cancelled = false;
        setSchedulesLoading(true);

        callApi(`/api/v1/practices/${practiceId}/pms/practitioners/${selectedPractitioner.id}/schedules/`)
            .then((r) => {
                if (cancelled) return;
                const results = r?.data?.results ?? r?.data ?? [];
                setSchedules(Array.isArray(results) ? results : []);
            })
            .catch(() => { if (!cancelled) setSchedules([]); })
            .finally(() => { if (!cancelled) setSchedulesLoading(false); });

        return () => { cancelled = true; };
    }, [practiceId, selectedPractitioner?.id]);

    // Load slots when practitioner + date + schedule are available
    const fetchSlots = useCallback(async () => {
        if (!practiceId || !selectedPractitioner?.id || !selectedDate) return;
        if (!schedules.length) return;

        setSlotsLoading(true);
        setSlotsError(null);
        setSlots([]);

        const dateStr = selectedDate.toISOString().split('T')[0];
        const endDate = new Date(selectedDate);
        endDate.setDate(endDate.getDate() + 1);
        const endStr = endDate.toISOString().split('T')[0];

        try {
            // Fetch slots from all schedules and merge
            const allSlots = [];
            await Promise.all(
                schedules.map(async (schedule) => {
                    const params = new URLSearchParams({
                        start: dateStr,
                        end: endStr,
                        duration: appointmentDuration,
                    });
                    const r = await callApi(
                        `/api/v1/practices/${practiceId}/pms/practitioners/${selectedPractitioner.id}/schedules/${schedule.id}/slots/?${params}`
                    );
                    const results = r?.data?.results ?? r?.data ?? [];
                    if (Array.isArray(results)) {
                        results.forEach((slot) => {
                            allSlots.push({
                                ...slot,
                                practitioner_id: selectedPractitioner.id,
                                _practitioner_name: selectedPractitioner.name || selectedPractitioner.full_name || '',
                                _schedule_id: schedule.id,
                            });
                        });
                    }
                })
            );

            // Sort by start time and remove duplicates
            const seen = new Set();
            const unique = allSlots
                .filter((s) => {
                    const key = `${s.start_time}`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                })
                .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

            setSlots(unique);
        } catch (err) {
            setSlotsError('Failed to load available slots. Please try again.');
        } finally {
            setSlotsLoading(false);
        }
    }, [practiceId, selectedPractitioner?.id, selectedDate, schedules, appointmentDuration]);

    useEffect(() => {
        // Clear slot when date or practitioner changes
        onSlotSelected(null);
        fetchSlots();
    }, [selectedPractitioner?.id, selectedDate?.toDateString()]);

    useImperativeHandle(ref, () => ({
        submit() {
            const missing = [];
            if (!selectedPractitioner) missing.push('Practitioner');
            if (!selectedDate) missing.push('Date');
            if (!pmsSlot) missing.push('Appointment slot');
            if (missing.length) {
                enqueueSnackbar(`Please select: ${missing.join(', ')}`, {
                    variant: 'warning',
                    anchorOrigin: { vertical: 'top', horizontal: 'center' },
                });
                return;
            }
            onSubmit(pmsSlot);
        },
    }), [selectedPractitioner, selectedDate, pmsSlot, onSubmit, enqueueSnackbar]);

    const { morning, afternoon, evening } = groupSlotsByPeriod(slots);
    const hasSlots = slots.length > 0;
    const showSlotArea = selectedPractitioner && selectedDate;

    return (
        <Box>
            {/* Practitioner selector */}
            <Box sx={sectionCardStyle}>
                <Typography sx={sectionLabelStyle}>
                    <Iconify icon="mdi:account-circle-outline" width={15} sx={{ color: 'primary.main' }} />
                    Practitioner
                </Typography>

                {practitionersError && <Alert severity="error" sx={{ mb: 1.5 }}>{practitionersError}</Alert>}

                {practitionersLoading ? (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">Loading practitioners…</Typography>
                    </Stack>
                ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {practitioners.map((p) => {
                            const isSelected = selectedPractitioner?.id === p.id;
                            const displayName = p.name || p.full_name || `Practitioner ${p.id}`;
                            return (
                                <Button
                                    key={p.id}
                                    variant={isSelected ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => onPractitionerSelected(isSelected ? null : p)}
                                    startIcon={<Iconify icon="mdi:account" width={14} />}
                                    sx={{
                                        borderRadius: '50px',
                                        textTransform: 'none',
                                        fontWeight: isSelected ? 700 : 400,
                                        fontSize: '0.8125rem',
                                        ...(isSelected
                                            ? {
                                                bgcolor: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.dark' },
                                            }
                                            : {
                                                border: '1px solid rgba(145,158,171,0.32)',
                                                color: 'text.secondary',
                                                bgcolor: 'transparent',
                                                '&:hover': { bgcolor: 'rgba(145,158,171,0.08)', borderColor: 'rgba(145,158,171,0.48)' },
                                            }),
                                    }}
                                >
                                    {displayName}
                                </Button>
                            );
                        })}
                        {!practitionersLoading && practitioners.length === 0 && (
                            <Typography variant="caption" color="text.disabled">
                                No practitioners configured for this practice.
                            </Typography>
                        )}
                    </Box>
                )}

                {schedulesLoading && selectedPractitioner && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                        <CircularProgress size={12} />
                        <Typography variant="caption" color="text.secondary">Loading schedule…</Typography>
                    </Stack>
                )}

                {selectedPractitioner && !schedulesLoading && schedules.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                        No schedules found for this practitioner.
                    </Alert>
                )}
            </Box>

            {/* Date picker */}
            <Box sx={sectionCardStyle}>
                <Typography sx={sectionLabelStyle}>
                    <Iconify icon="mdi:calendar" width={15} sx={{ color: '#00AB55' }} />
                    Date
                </Typography>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label="Select date"
                        value={selectedDate}
                        onChange={onDateSelected}
                        disablePast
                        slotProps={{
                            textField: {
                                size: 'small',
                                fullWidth: true,
                            },
                        }}
                    />
                </LocalizationProvider>
            </Box>

            {/* Available slots */}
            {showSlotArea && (
                <Box sx={sectionCardStyle}>
                    <Typography sx={sectionLabelStyle}>
                        <Iconify icon="mdi:clock-outline" width={15} sx={{ color: '#FFAB00' }} />
                        Available Slots
                        {pmsSlot && (
                            <Chip
                                icon={<Iconify icon="mdi:check" width={12} />}
                                label={formatSlotTime(pmsSlot.start_time)}
                                size="small"
                                color="success"
                                sx={{ ml: 'auto', fontWeight: 700, fontSize: '11px' }}
                            />
                        )}
                    </Typography>

                    {slotsError && <Alert severity="error" sx={{ mb: 1.5 }}>{slotsError}</Alert>}

                    {slotsLoading && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
                            <CircularProgress size={18} sx={{ color: 'primary.main' }} />
                            <Typography variant="body2" color="text.secondary">Checking availability…</Typography>
                        </Stack>
                    )}

                    {!slotsLoading && !slotsError && schedules.length > 0 && (
                        <>
                            {!hasSlots && (
                                <Box sx={{ textAlign: 'center', py: 4, px: 2, bgcolor: 'rgba(145,158,171,0.04)', borderRadius: 2, border: '1px dashed', borderColor: 'rgba(145,158,171,0.3)' }}>
                                    <Iconify icon="mdi:calendar-remove-outline" width={32} sx={{ color: 'text.disabled', mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">No slots available on this date.</Typography>
                                    <Typography variant="caption" color="text.disabled">Try a different date or practitioner.</Typography>
                                </Box>
                            )}

                            <SlotPeriodSection
                                label="Morning"
                                icon="mdi:weather-sunrise"
                                iconColor="#FF9800"
                                slots={morning}
                                selectedSlot={pmsSlot}
                                onSelect={onSlotSelected}
                            />
                            <SlotPeriodSection
                                label="Afternoon"
                                icon="mdi:weather-sunny"
                                iconColor="#F59E0B"
                                slots={afternoon}
                                selectedSlot={pmsSlot}
                                onSelect={onSlotSelected}
                            />
                            <SlotPeriodSection
                                label="Evening"
                                icon="mdi:weather-sunset"
                                iconColor="#7C3AED"
                                slots={evening}
                                selectedSlot={pmsSlot}
                                onSelect={onSlotSelected}
                            />
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
});

export default PmsAvailabilityWizardStep;
